import { sum } from 'lodash-es'
import { useMemo } from 'react'

import {
  CoverOption,
  getAdditionalItemsTotals,
  getPremium,
  getTaxAmount,
  getTotalPremium,
  inForce,
  isCoverActive,
  Quote,
} from '../domain'
import { PaymentScheduleType, ProductConfig } from '../state/configuration'
import {
  useAdditionalPremiumBreakdown,
  useDiscountCovers,
  useMainCovers,
  useOptionalCovers,
} from './covers'

export interface QuotePremiums {
  breakdown: {
    main: number
    discount: number
  }
  additionalBreakdown: Record<string, number>
  totals: {
    gross: number
    yearly: number
    discounted: number
    taxAmount: number
  }
  optional: CoverOption[]
}

export const useQuotePremiums = (
  quote: Quote | undefined,
  product: ProductConfig,
  type?: PaymentScheduleType,
): QuotePremiums => {
  const allMainCovers = useMainCovers(quote, type)
  const allOptionalCovers = useOptionalCovers(quote, product, type)
  const allDiscountCovers = useDiscountCovers(quote, type)
  const allAdditionalItems = useAdditionalPremiumBreakdown(quote, type)

  return useMemo(() => {
    const mainCovers = allMainCovers.filter(isCoverActive)
    const optionalCovers = allOptionalCovers.filter((it) => it.active)
    const discountCovers = allDiscountCovers.filter(inForce)
    const additionalItems = allAdditionalItems.filter((it) => it.active)

    const mainPremium = getPremium(mainCovers)
    const mainYearlyPremium = getTotalPremium(mainCovers)
    const optionalPremium = sum(optionalCovers.map((it) => it.premium))
    const optionalYearlyPremium = sum(optionalCovers.map((it) => it.totalPremium))
    const discountPremium = Math.abs(getPremium(discountCovers))

    const mainTaxAmount = getTaxAmount(mainCovers)
    const optionalTaxAmount = sum(optionalCovers.map((it) => it.taxAmount))
    const discountTaxAmount = Math.abs(getTaxAmount(discountCovers))

    const grossTotal = mainPremium + optionalPremium
    const grossYearlyTotal = mainYearlyPremium + optionalYearlyPremium
    const discountedTotal = grossTotal - discountPremium
    const taxAmountTotal = mainTaxAmount + optionalTaxAmount - discountTaxAmount

    const additionalItemsTotals = getAdditionalItemsTotals(additionalItems)

    return {
      breakdown: {
        main: mainPremium,
        discount: discountPremium,
      },
      additionalBreakdown: additionalItemsTotals,
      totals: {
        gross: grossTotal,
        yearly: grossYearlyTotal,
        discounted: discountedTotal,
        taxAmount: taxAmountTotal,
      },
      optional: optionalCovers,
    } satisfies QuotePremiums
  }, [allMainCovers, allOptionalCovers, allDiscountCovers, allAdditionalItems])
}

export const hasDiscount = (amounts: QuotePremiums['breakdown']) => amounts.discount > 0
